import Canvas from './scalable-canvas'
import $ from 'jquery'
import _ from 'lodash'
import closestUntil from './support/closest-until'

/*
 * FullscreenCanvas is an extension of the scalable canvas, meant to be viewed at fullscreen, duh.
 * It's also rotatable.
 */
var FullscreenCanvas = Canvas.extend({

  className: 'scalable-canvas fullscreen-canvas', // both

  rotation: 0, // by default there is no rotation

  build: function () {
    // Register an event handler on the parent canvas container to handle click events
    // We have to register it on the container b/c the canvas itself might not take up the full container when it's zoomed
    // but we want that extra space to be able to zoom out on click
    this.getParent().on('click.canvas-zoom', this.zoom.bind(this))
                    .addClass('fullscreen-canvas-container')

    return Canvas.prototype.build.apply(this, arguments)
  },

  /*
   * Sets the rotation to the given angle
   */
  rotate: function (deg) {
    var r = (90 * (Math.round(deg / 90))) % 360
    this.rotation = (r * 360 < 0) ? r + 360 : r

    if (this.rotation) {
      var container = this.$el.parent('.rotate-container')
      if (!container.length) {
        this.$el.wrapAll('<div class="rotate-container">')
        container = this.$el.parent('.rotate-container')
      }

      container.removeClass(function (index, css) {
          var matches = css.match(/rotate-\d+|/g)
          if (matches) {
            return matches.join(' ')
          }
        })
        .addClass('rotate-' + this.rotation)
    } else if (this.$el.parent('.rotate-container').length) {
      this.$el.unwrap()
    }

    this.resize()

    return this
  },

  rotateResize: function () {
    if (this.rotation === 0) {
      return
    }
    // determine the transform origin for the rotation
    var x = 0
      , y = 0
      , parent = this.$el.parent()
      , width = parent.width()
      , height = parent.height()

    if (this.rotation === 90) {
      x = y = width / 2
    } else if (this.rotation === 180) {
      x = width / 2
      y = height / 2
    } else if (this.rotation === 270) {
      x = y = height / 2
    }

    // Set the transform origin for the rotation
    this.$el.parent('.rotate-container')
            .css('transform-origin', x + 'px ' + y + 'px')
  },

  dimensions: function () {
    // Grab the dimensions, but we may need to swap parent width/height if we're rotated
    var dim = Canvas.prototype.dimensions.apply(this, null)
    if (this.rotation === 90 || this.rotation === 270) {
      dim.parentHeight = dim.actualParentWidth
      dim.parentWidth = dim.actualParentHeight
    } else {
      dim.parentHeight = dim.actualParentHeight
      dim.parentWidth = dim.actualParentWidth
    }
    return dim
  },

  sizeStyles: function () {
    var dim = this.dimensions()
      , containerAspectRatio = dim.parentHeight / dim.parentWidth // Parent element of the canvas (fullscreen)
      , canvasAspectRatio = dim.contentHeight / dim.contentWidth // actual size of the canvas, based on wobs, never changes with browser resize
      , spacing = _.zipObject(['top', 'bottom', 'left', 'right'], [0, 0, 0, 0])
      , scale, dimensions = {}

    if (containerAspectRatio > canvasAspectRatio) {
      // The canvas is too wide, so we want to letterbox the top and bottom.
      scale = dim.parentWidth / dim.contentWidth
      var inverseScale = dim.contentWidth / dim.parentWidth

      spacing.top = (0.5 * (dim.parentHeight - (dim.contentHeight * scale))) * inverseScale
      spacing.bottom = spacing.top

      // Set the width so that after it's scaled it will fill the parent 100%.
      // Note: we don't want to set the height to auto because that makes the canvas background not cover everything.
      var inverseScaleNoPadding = dim.width / dim.actualParentWidth
      dimensions.width = (100 * inverseScaleNoPadding) + '%'
    } else {
      // The canvas is too tall, so we want to letterbox the right and left.
      // Most of the defaults will need to be overridden.
      scale = dim.parentHeight / dim.contentHeight
      var inverseScale = dim.contentHeight / dim.parentHeight

      spacing.left = (0.5 * (dim.parentWidth - (dim.contentWidth * scale))) * inverseScale
      spacing.right = spacing.left

      // Set the height so that after it's scaled it will fill the parent 100%.
      // Note: we can set the width to auto, but it doesn't seem to do anything.
      var inverseScaleNoPadding = dim.height / dim.actualParentHeight
      dimensions.height = (100 * inverseScaleNoPadding) + '%'
    }

    // These need to be set for every mode
    dimensions.transform = 'scale(' + scale + ')'

    var self = this
    // Sets spacing and padding. css padding for positive, so the background image shows up
    // and css margin for negatives since since you can't have negative padding
    Object.keys(spacing).forEach(function (prop) {
      if (spacing[prop] > 0) {
        dimensions['padding-' + prop] = spacing[prop] + self.padding + 'px'
      } else {
        dimensions['padding-' + prop] = self.padding + 'px' // sets it to padding
        dimensions['margin-' + prop] = spacing[prop] + 'px' // sets it to 0
      }
    })
    this._letterbox = spacing
    return dimensions
  },

  zoom: function (e) {
    if (!this.getParent().length) {
      // The canvas isn't in the DOM so we can't reliably figure out the zoom dimensions.
      // This could happen if the the canvas is removed from the DOM as part of a click event, e.g. when doing a drilldown in SB3
      return
    }

    // If they clicked within an interactive widget, ignore zooms.
    if (closestUntil(e.target, '.is-interactive', '.canvas').length) {
      return
    }

    // Make sure we're not showing the letterbox
    this.$('.canvas-letterbox').remove()

    if (this.getParent().hasClass('canvas-zoomed')) {
      this._zoomOut()
      this.highlightLetterbox()
    } else {
      this._zoomIn(e)
    }
    this.trigger('zoom')
  },

  _zoomIn: function (e) {
    let dimensions = this.dimensions()
      , parent = this.$el.parents('.' + this.parentClass)
      , parentEl = parent.get(0)
      , styles = {
          padding: this.padding,
          margin: 0,
        }
      , coords = e && _.mapValues(this.coords(e), coord => coord += this.padding) // Remove the padding on the canvas so we get the event click pos within the canvas-container

    this.$el.height(dimensions.height)
            .width(dimensions.width)

    if (this._letterbox.left) {
      // 100% width and there can be vertical scrollbars
      // Determine the new scale based on the widths
      let scale = dimensions.parentWidth / dimensions.contentWidth
        , bottom = (dimensions.contentHeight * scale) - dimensions.contentHeight
        , clickPercentage = coords && (coords.top / dimensions.parentHeight) // Determine where they clicked as a percentage

      if (bottom < 0) {
        // We had to shrink the objects, make sure there isn't too much space on the bottom
        styles['margin-bottom'] = `${bottom}px` // Chop off the excess using a negative margin
      }

      // Full height so there are vertical scrollbars
      parent.addClass('canvas-zoomed canvas-zoomed-full-height')
      styles.transform = `scale(${scale})`
      this.$el.css(styles)

      if (coords) {
        parent.scrollTop((parentEl.scrollHeight * clickPercentage) - dimensions.parentHeight / 2) // subtracts half the viewport height so it's centered
      }
    } else {
      // We want 100% height and there can be horizontal scrollbars
      // Determine the new scale based on the heights
      let scale = dimensions.parentHeight / dimensions.contentHeight
        , clickPercentage = coords && (coords.left / dimensions.parentWidth) // click position as a percentage

      // Full width so there are horizontal scrollbars
      parent.addClass('canvas-zoomed canvas-zoomed-full-width')
      styles.transform = `scale(${scale})`
      this.$el.css(styles)

      if (coords) {
        parent.scrollLeft((parentEl.scrollWidth * clickPercentage) - dimensions.parentWidth / 2) // centers the previous click target after we adjusted zoom
      }
    }
  },

  _zoomOut: function () {
    // Remove the classes on our parent that allow scrollbars
    this.$el.parents('.' + this.parentClass)
            .removeClass('canvas-zoomed canvas-zoomed-full-width canvas-zoomed-full-height')
            .scrollTop(0) // Reset the scroll positions
            .scrollLeft(0)

    // Resize the canvas so it sets its proper styles
    this.resize()
  },

  highlightLetterbox: function () {
    var fullSpace = 'calc(100% + ' + this.padding * 2 + 'px)'

    if (this._letterbox.left) {
      // Extra spacing on right/left
      var css = {
          top: 0,
          width: this._letterbox.left,
          height: fullSpace
        }
        , lb1 = $('<div>').addClass('canvas-letterbox')
                          .css(css)
                          .css('left', 0)
        , lb2 = $('<div>').addClass('canvas-letterbox')
                          .css(css)
                          .css('left', this.dimensions().contentWidth + this._letterbox.left)
    } else {
      // Extra spacing on top/bottom
      var css = {
          left: 0,
          width: fullSpace,
          height: this._letterbox.top
        }
        , lb1 = $('<div>').addClass('canvas-letterbox')
                          .css(css)
                          .css('top', 0)
        , lb2 = $('<div>').addClass('canvas-letterbox')
                          .css(css)
                          .css('top', this.dimensions().contentHeight + this._letterbox.top)
    }

    this.$el.append(lb1)
            .append(lb2)
    this.$('.canvas-letterbox').delay(2000).fadeOut(1000, function () {
      $(this).remove()
    })
  },

  resize: function () {
    if (!this.collection.ready) {
      // Ignore resizing the canvas until all wobs are ready so we don't thrash the browser
      return
    }

    if (this.getParent().hasClass('canvas-zoomed')) {
      // Re-run zoom logic so the zoomed canvas is properly adjusted
      this._zoomIn()
    } else {
      // Don't worry about resizing the canvas if we're zoomed
      Canvas.prototype.resize.apply(this, null)
      this.rotateResize()
    }
    return this
  },

  release: function () {
    Canvas.prototype.release.apply(this, arguments)
    this.getParent().off('.canvas-zoom')
                    .removeClass('fullscreen-canvas-container')
  }

})

export default FullscreenCanvas
